/**
 * Parser del CSV de Konfio TDC.
 *
 * Header conocido:
 *   Fecha operacion,Descripcion,RFC,Monto ($),Monto en Moneda extranjera,
 *   Tipo de cambio,Divisa,No autorizacion,Tipo,Estatus,Integrante,Número,
 *   Tarjeta,Status Conciliacion,Folios de facturas,Ticket adjunto,
 *   Nombre del comercio,Hora de transacción,Monto IVA,Monto Total Ticket,...
 *
 * Convenciones:
 *   - Monto positivo + Tipo (CARGO|COMPRA) → gasto cargado a la TDC.
 *   - Monto negativo + Tipo "PAGO" + Descripción "PAGO VIA SPEI" → pago a la TDC
 *     desde otra cuenta propia (ej. BBVA → Konfio). Es transfer interno entrante.
 */
import type { BulkParseResult, BulkParsedRow } from "./types";

export function parseKonfioCsv(text: string): BulkParseResult {
  const errors: string[] = [];
  const rows: BulkParsedRow[] = [];

  const lines = text.split(/\r?\n/);
  if (lines.length < 2) return { bank: "konfio", rows, errors: ["CSV vacío"] };

  const header = parseCsvLine(lines[0]!);
  const idx = (col: string) =>
    header.findIndex((h) => h.toLowerCase().trim() === col.toLowerCase());

  const iDate = idx("Fecha operacion");
  const iDesc = idx("Descripcion");
  const iAmount = idx("Monto ($)");
  const iTipo = idx("Tipo");
  const iIva = idx("Monto IVA");
  if (iDate < 0 || iDesc < 0 || iAmount < 0) {
    return {
      bank: "konfio",
      rows,
      errors: ["Header de Konfio no reconocido — ¿formato cambió?"],
    };
  }

  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line || !line.trim()) continue;
    try {
      const fields = parseCsvLine(line);
      const dateRaw = fields[iDate] ?? "";
      const desc = fields[iDesc] ?? "";
      const amountRaw = fields[iAmount] ?? "";
      const tipo = (fields[iTipo] ?? "").trim().toUpperCase();
      const ivaRaw = iIva >= 0 ? (fields[iIva] ?? "") : "";

      const date = parseKonfioDate(dateRaw);
      if (!date) {
        errors.push(`Fila ${i}: fecha inválida "${dateRaw}"`);
        continue;
      }
      const amountCents = parsePesos(amountRaw);
      if (amountCents === 0) continue;

      // PAGO con monto negativo = pago a la TDC desde otra cuenta. Esto es
      // un transfer interno entrante a la TDC (reduce deuda). Una TDC NUNCA
      // tiene ingresos reales — todo abono es pago a crédito.
      if (tipo === "PAGO" && amountCents < 0) {
        rows.push({
          date,
          description: desc ? `Pago a TDC — ${desc}` : "Pago a TDC",
          kind: "ingreso", // entra a la TDC (reduce deuda)
          amountCents: Math.abs(amountCents),
          ivaCents: 0,
          internalTransferHint: {
            kind: "transfer_in",
            matchKey: `${date}-${Math.abs(amountCents)}`,
          },
          rawCode: tipo,
        });
        continue;
      }

      // Cualquier otro: cargo / compra → gasto.
      rows.push({
        date,
        description: desc,
        kind: "gasto",
        amountCents: Math.abs(amountCents),
        ivaCents: ivaRaw ? parsePesos(ivaRaw) : 0,
        rawCounterparty: desc.split(" ").slice(0, 4).join(" "),
        rawCode: tipo,
      });
    } catch (e) {
      errors.push(`Fila ${i}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return { bank: "konfio", rows, errors };
}

/** Parsea una línea CSV respetando comillas. Suficiente para Konfio. */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function parseKonfioDate(s: string): string | null {
  // Konfio ya viene en YYYY-MM-DD.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}

function parsePesos(s: string): number {
  if (!s) return 0;
  const n = parseFloat(s.replace(/[$,\s]/g, ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}
